import { Component } from '@angular/core';

import { Subscription } from 'rxjs';

import { SDKDataGridColumn, SDKDataGridOptions, Filters } from 'sdk-datagrid';

import { HttpService } from '~/core/services/http.service';
import { FormatterService } from '~/core/services/formatter.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { WellsApiService } from '~/services/wells-api.service';

import { BaseComponent } from '~/pages/base.component';
import { Clone } from '~/core/utils/clone';

@Component({
    selector: 'data',
    templateUrl: './data.component.html',
    styleUrls: ['./data.component.scss'],
    standalone: false
})

export class DataComponent extends BaseComponent {
    public activeWell: string = "";
    public activeSchema: string = "";
    public wells: any[] = [];
    public schemas: any[] = [];
    
    public columns: SDKDataGridColumn[] = [
        { Name: "field", DisplayName: "Field", FilterTypes: [Filters.Contains] },
        { Name: "value", DisplayName: "Value", FilterTypes: [Filters.Contains] },
        { Name: "type", DisplayName: "Type", FilterTypes: [Filters.Contains] },
        { Name: "description", DisplayName: "Description", FilterTypes: [Filters.Contains], showFilter: false },
    ];
    public options: SDKDataGridOptions = {};

    public data: any;
    public dataExport: any;
    public rows: number | undefined;
    public page: number | undefined;
    public total: number | undefined;
    public error: string = "";
    public updateGrid: boolean = true;
    public isAuthenticated: boolean = false;
    public selectedRecord: any = null;
    public modifiedRecords: any[] = [];

    private dataSubscription!: Subscription;
    private wellsSubscription!: Subscription;
    private schemasSubscription!: Subscription;
    private rawData: any;

    constructor(
        private httpService: HttpService,
        private formatterService: FormatterService,
        private appSettingsService: AppSettingsService,
        private wellsApiService: WellsApiService,
    ) {
        super();
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        await super.ngOnInit();

        this.isLoading = true;
        
        // Try to authenticate with Wells API
        await this.authenticateWithWellsAPI();
    }

    public async ngOnDestroy() {
        if (this.dataSubscription) {
            this.dataSubscription.unsubscribe();
        }
        if (this.wellsSubscription) {
            this.wellsSubscription.unsubscribe();
        }
        if (this.schemasSubscription) {
            this.schemasSubscription.unsubscribe();
        }
    }

    //*************************************************************************
    //  Authentication Methods
    //*************************************************************************
    public async authenticateWithWellsAPI() {
        try {
            
            const settings = await this.appSettingsService.getSettings();
            const email = settings?.user?.username || settings?.user?.email || 'developer@chevron.com';
            
            this.httpService.issueWellsToken(email).subscribe({
                next: (response: any) => {
                    this.isAuthenticated = true;
                    this.error = "";
                    
                    // Wait a moment to ensure token is set before making API calls
                    setTimeout(() => {
                        this.loadWells();
                    }, 100);
                },
                error: (error: any) => {
                    this.error = `Authentication failed: ${error.message || error.error?.detail || JSON.stringify(error)}`;
                    this.isAuthenticated = false;
                    this.isLoading = false;
                }
            });
        } catch (error: any) {
            this.error = `Authentication error: ${error.message}`;
            this.isAuthenticated = false;
            this.isLoading = false;
        }
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public async loadWells() {
        try {
            this.isLoading = true;

            this.wellsSubscription = this.httpService.getWellsList().subscribe({
                next: (response: any) => {
                    // The response now contains wells array and pagination info
                    this.wells = response.wells || [];
                    this.error = "";
                    
                    if (this.wells.length > 0) {
                        this.activeWell = this.wells[0].name || this.wells[0];
                        this.loadSchemas();
                    } else {
                        this.error = "No wells available. Please check your access permissions.";
                        this.isLoading = false;
                    }
                },
                error: (error: any) => {
                    this.wells = [];
                    this.error = `Failed to load wells: ${error.status} ${error.statusText} - ${error.message || JSON.stringify(error.error)}`;
                    this.isLoading = false;
                }
            });

        } catch (error: any) {
            this.error = `Error loading wells: ${error.message}`;
            this.isLoading = false;
        }
    }

    public async loadSchemas() {
        if (!this.activeWell) {
            return;
        }

        try {
            this.schemasSubscription = this.wellsApiService.getSchemaList(this.activeWell).subscribe({
                next: (response: any) => {
                    this.schemas = Array.isArray(response) ? response : (response.schemas || []);
                    this.error = "";
                    
                    if (this.schemas.length > 0) {
                        this.activeSchema = this.schemas[0].name || this.schemas[0];
                        this.loadData();
                    } else {
                        this.isLoading = false;
                    }
                },
                error: (error: any) => {
                    this.schemas = [];
                    this.error = `Failed to load schemas: ${error.message}`;
                    this.isLoading = false;
                }
            });

        } catch (error: any) {
            this.error = `Error loading schemas: ${error.message}`;
            this.isLoading = false;
        }
    }

    public async loadData(event: any = null) {
        if (!this.activeWell || !this.activeSchema) {
            return;
        }

        try {
            this.isLoading = true;
            // Reset modified records when loading new data
            this.modifiedRecords = [];

            this.dataSubscription = this.wellsApiService.getSchema(this.activeWell, this.activeSchema,true).subscribe({
                next: (response: any) => {
                    this.rawData = response;
                    this.error = "";

                    this.processData(event);
                },
                error: (error: any) => {
                    this.rawData = null;
                    this.error = `Failed to load schema data: ${error.message}`;
                    this.isLoading = false;
                }
            });

        } catch (error: any) {
            this.error = `Error loading data: ${error.message}`;
            this.isLoading = false;
        }
    }

    public onWellChange() {
        this.activeSchema = "";
        this.schemas = [];
        this.data = [];
        this.loadSchemas();
    }

    public onSchemaChange() {
        this.data = [];
        this.loadData();
    }

    public showDetails(event: any) {
        if (event?.record) {
            this.selectedRecord = event.record;
            // Could open a modal with detailed field information
        }
    }

    public addRecord() {
        // Add a blank record to the data array for editing
        if (!this.data) this.data = [];
        
        const newRecord = { field: '', value: '', type: '', description: '' };
        this.data.unshift(newRecord);
        
        // Track the new record in our modified records array
        this.modifiedRecords.push({
            action: 'add',
            record: newRecord
        });
        
        this.updateGrid = true;
    }
    
    /**
     * Add an empty record at the end of the data grid
     * This is specifically for the "Add Record" button
     */
    public addEmptyRecord() {
        if (!this.data || this.data.length === 0) return;
        
        // Create a new record with the same structure as the first record (record1)
        // but with empty values
        const template = this.data[0];
        const newRecord: any = {};
        
        // Copy the structure but set all values to empty strings
        Object.keys(template).forEach(key => {
            newRecord[key] = '';
        });
        
        // Add the record to the end of the current data
        this.data.push(newRecord);
        
        // Track the new record in modified records array
        this.modifiedRecords.push({
            action: 'add',
            record: newRecord
        });
        
        this.updateGrid = true;
        
        // Scroll to the bottom of the grid to show the new record
        setTimeout(() => {
            const gridElement = document.querySelector('.sdk-datagrid-container');
            if (gridElement) {
                gridElement.scrollTop = gridElement.scrollHeight;
            }
        }, 100);
    }
    
    public deleteSelectedRecord() {
        if (!this.selectedRecord) return;
        
        // Find the index of the selected record
        const index = this.data.findIndex((record: any) => 
            record.field === this.selectedRecord.field && 
            record.value === this.selectedRecord.value);
        
        if (index !== -1) {
            // Track the deletion in our modified records array
            this.modifiedRecords.push({
                action: 'delete',
                record: Clone.deepCopy(this.selectedRecord)
            });
            
            // Remove the record from the data array
            this.data.splice(index, 1);
            this.selectedRecord = null;
            this.updateGrid = true;
        }
    }
    
    public async saveSuggestions() {
        if (this.modifiedRecords.length === 0) {
            this.error = "No changes to save";
            return;
        }
        
        try {
            this.isLoading = true;
            
            // Get user ID from app settings
            const settings = await this.appSettingsService.getSettings();
            const userId = settings?.user?.username || settings?.user?.email || 'unknown';
            console.log(this.activeSchema,'schema')
            
            this.httpService.saveEngineerSuggestions(
                this.activeWell,
                this.activeSchema,
                userId,
                this.modifiedRecords
            ).subscribe({
                next: (response: any) => {
                    // Clear the modified records after successful save
                    this.modifiedRecords = [];
                    this.isLoading = false;
                    
                    // Show success message (could be enhanced with a toast notification)
                    this.error = "Suggestions saved successfully";
                    
                    // Reload data to get the latest from the server
                    setTimeout(() => {
                        this.loadData();
                    }, 1000);
                },
                error: (error: any) => {
                    this.isLoading = false;
                    this.error = `Failed to save suggestions: ${error.message || JSON.stringify(error)}`;
                }
            });
        } catch (error: any) {
            this.isLoading = false;
            this.error = `Error saving suggestions: ${error.message}`;
        }
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private processData(event: any = null) {
        let page = 1;
        let rows = 100;
        let start = (page - 1) * rows;
        let end = page * rows;

        if (event) {
            page = parseInt(event.page);
            rows = parseInt(event.rows);
            start = (page - 1) * rows;
            end = page * rows;
        }

        let _tmp: any = Clone.deepCopy(this.rawData);
        let _data: any = [];

        // Convert schema object to flat array of field data
        if (_tmp && typeof _tmp === 'object') {
            _data = this.flattenSchemaData(_tmp);
        }

        _data = this.filterData(_data);
        _data = this.sortData(_data);

        this.total = _data.length;

        if (event && event.action === "Export Data") {
            this.dataExport = _data;
            this.isLoading = false;
        } else {
            this.data = _data.slice(start, end);
            this.rows = rows;
            this.page = page;

            setTimeout(() => {
                this.updateGrid = true;
                this.isLoading = false;
            }, 100);
        }
    }

    private flattenSchemaData(schemaData: any, prefix: string = ''): any[] {
        const result: any[] = [];
        
        if (!schemaData || typeof schemaData !== 'object') {
            return result;
        }

        for (const [key, value] of Object.entries(schemaData)) {
            const fieldName = prefix ? `${prefix}.${key}` : key;
            
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                // Recursively flatten nested objects
                result.push(...this.flattenSchemaData(value, fieldName));
            } else {
                result.push({
                    field: fieldName,
                    value: this.formatValue(value),
                    type: this.getValueType(value),
                    description: this.getFieldDescription(fieldName, value)
                });
            }
        }
        
        return result;
    }

    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (Array.isArray(value)) {
            return `[${value.length} items]`;
        }
        if (typeof value === 'object') {
            return '[Object]';
        }
        return String(value);
    }

    private getValueType(value: any): string {
        if (value === null || value === undefined) {
            return 'null';
        }
        if (Array.isArray(value)) {
            return 'array';
        }
        return typeof value;
    }

    private getFieldDescription(fieldName: string, value: any): string {
        // You can add custom field descriptions here based on field names
        const descriptions: { [key: string]: string } = {
            'well_name': 'Name of the well',
            'schema_type': 'Type of schema configuration',
            'depth': 'Well depth measurement',
            'location': 'Geographic location of the well'
        };
        
        return descriptions[fieldName] || `${fieldName} field`;
    }

    private filterData(data: any) {
        this.columns.filter((column: SDKDataGridColumn) => column.Filter).forEach((column: SDKDataGridColumn) => {
            if (!column.Filter || !column.Filter.operation) return;
            switch (column.Filter.operation.toString()) {
                case Filters.Contains:
                    data = data.filter((d: any) => 
                        d[column.Name] && 
                        d[column.Name].toString().toLowerCase().includes(column.Filter!.value.toString().toLowerCase())
                    ).slice(0);
                    break;
                case Filters.GreaterThanOrEqual:
                    data = data.filter((d: any) => d[column.Name] >= column.Filter!.value).slice(0);
                    break;
                case Filters.LessThanOrEqual:
                    data = data.filter((d: any) => d[column.Name] <= column.Filter!.value).slice(0);
                    break;
                default:
                    break;
            }
        });

        return data;
    }

    private sortData(data: any) {
        this.columns
            .filter((column: SDKDataGridColumn) => column.Sort)
            .sort((a: SDKDataGridColumn, b: SDKDataGridColumn) => ((a.Sort?.position ?? 0) > (b.Sort?.position ?? 0)) ? 1 : -1)
            .forEach((column: SDKDataGridColumn) => {
                data = data.sort((a: any, b: any) => (a[column.Name] > b[column.Name]) ? 1 : -1).slice(0);
            });

        return data;
    }
}
